import { TableData, TableHeader } from "../Table/DataTable";
import StructureComponent from "./GridComponentTypes/StructureComponent";
import { ChangeEvent, Dispatch, SetStateAction } from "react";
import PlotComponent from "../Popups/ModelDetailsPopupComponents/PlotComponent";
import StatsValue from "./GridComponentTypes/StatsValue";
import DetailsModal from "../Popups/DetailsModal";
import CustomCheckbox from "../Form/CustomCheckbox";
import { Skeleton } from "@mui/material";


export default function DataGrid({
    isLoading,
    gridData,
    headers,
    statsType,
    threshold,
    selection,
    setPopupMessage
}: {
    isLoading: boolean,
    gridData: TableData[],
    headers: TableHeader[],
    statsType?: 'model' | 'prediction',
    threshold?: number,
    selection?: {
        selectedItems: (string | number)[],
        setSelectedItems: Dispatch<SetStateAction<(string | number)[]>>,
    },
    setPopupMessage: (popupMessage: string, isSuccessMessage: boolean) => void,

}) {

    const filterHeaders = () => {
        const shownHeaders = headers.filter(header => header.isShown);
        let filteredHeaders: TableHeader[] = [];

        shownHeaders.forEach(header => {
            if (header.value.toLocaleLowerCase().includes('smile')) {
                if (header.label === 'Chemical Structure') {
                    filteredHeaders.push(header)
                }
            } else {
                filteredHeaders.push(header);
            }
        })

        return filteredHeaders.slice(0,5)
    };

    const handleItemSelection = (e:ChangeEvent<HTMLInputElement>) => {
        if (selection) {
            const { selectedItems, setSelectedItems } = selection;
            const id = e.target.value;
            if (e.target.checked) {
                setSelectedItems(selectedItems.concat(id));
            } else {
                setSelectedItems(selectedItems.filter(item => item !== id));
            }
        }
    };

    const checkIfHasPlots = (data: TableData[]) => {
        if (!data || data.length === 0 || !data[0].fields) {
            return false;
        }

        const visField = data[0].fields.filter(field => field.type === 'structure' || field.type === 'plot');
        if (visField.length > 0 && visField[0].type === 'plot') {
            return true;
        } else {
            return false;
        }
    };

    const getMethodLink = (data: TableData) => {
        const methodField = data.fields.filter(field => field.associatedColumn === 'method');
        if (methodField.length > 0) {
            return methodField[0].action!;
        }
    }


    return (
        <div className="grow overflow-y-auto border-t border-b border-outline">
            <div className={`w-full data-grid ${checkIfHasPlots(gridData) ? 'model' : ''} py-6`}>
                {isLoading ? (
                    <>
                    {Array.from(Array(8).keys()).map(k => (
                        <Skeleton key={k} variant='rounded' width='100%' height={400}/>
                    ))}
                    </>
                ) : (
                    <>      
                    {gridData.map((data, dataIndex) => {
                         const visField = data.fields.filter(field => field.type === 'structure' || field.type === 'plot');
                         const methodLink = getMethodLink(data);

                        return (
                        <div key={dataIndex} className={`w-full relative overflow-hidden rounded-xl bg-white border border-outline flex flex-col justify-start items-stretch`}>
                           {selection && (
                                <div className="z-20 absolute top-0 left-0 p-7 flex justify-start items-center">
                                    <CustomCheckbox
                                        checked={selection.selectedItems.includes(data.id)}
                                        handleChange={handleItemSelection}
                                        value={data.id}
                                    />
                                </div>
                           )}
                           {selection && selection.selectedItems.includes(data.id) && (
                                <div className="z-10 absolute top-0 left-0 right-0 bottom-0 bg-[#D9DBE7]/[0.4]"></div>
                           )}
                           {visField.length > 0 ? (
                            <>
                            {visField[0].type === 'structure' ? (
                                <StructureComponent 
                                    value={visField[0].value as string}
                                />
                            ) : (
                                <PlotComponent 
                                    plotFields={visField}
                                />
                            )}
                            </>
                           ) : (
                            <></>
                           )}
            
                            <div className="w-full grow flex flex-col justify-start items-stretch gap-6 p-6 border-t border-outline">
                                {filterHeaders().map(header => {
                                    const matchingData = data.fields.find(field => field.associatedColumn === header.value);
                                    if (matchingData) {
                                        if (matchingData.type === 'structure' || matchingData.associatedColumn === 'method') {
                                            return (
                                                <div className="grow">
                                                    <p className={`font-semibold text-default text-[16px] max-w-full truncate`}>
                                                        {matchingData.value}
                                                    </p>
                                                </div>
                                            )
                                        } else {
                                            return (
                                                <div className="w-full flex-col justify-start items-start gap-6">
                                                    <div className="flex justify-start items-center gap-2">
                                                        <p className="text-[12px] font-semibold text-disable">{header.label}</p>
                                                        {header.columnMetadata}
                                                    </div>
                                                    
                                                    {matchingData.type === 'stat' && statsType !== undefined? (
                                                        <StatsValue 
                                                        name={matchingData.associatedColumn} 
                                                        value={matchingData.value as string} 
                                                        statsType={statsType}
                                                        threshold={threshold}
                                                    />
                                                    ) : matchingData.type === 'text' ? (
                                                        <p className="text-default text-[14px] font-medium">{matchingData.value}</p>
                                                    ) : (
                                                        <></>
                                                    )}
                                                    
                                                </div>
                                            )
                                        }
                                    } else {
                                        return null;
                                    }
                                })}
                            </div>

                            {methodLink ? (
                                <button onClick={(e:any) => methodLink(e)} className="w-full bg-[#F7F6FB] text-disable font-semibold text-[12px] p-3 hover:bg-secondary hover:text-primary transition-all">
                                    See more details
                                </button>
                            ) : (
                                <DetailsModal 
                                    data={data}
                                    isGridItem
                                    setPopupMessage={setPopupMessage}
                                />
                            )}
                        </div>
                    )})}
                    </>
                    )}
            </div>
        </div>
    )
}